import { MessageSquare, PanelRight } from 'lucide-react';
import { useComments } from '../comments/CommentContext';

// Two buttons in the TopBar:
//  - Comment mode toggle — enables click-to-comment + reveals pins
//  - Sidebar toggle — opens/closes the right-side list of all comments
// Both can be on or off independently. The mode toggle carries a count
// badge in its OFF state so reviewers know feedback exists at a glance.
export function CommentToggles({ vertical = false }: { vertical?: boolean }) {
  const {
    commentMode,
    toggleCommentMode,
    sidebarOpen,
    toggleSidebar,
    comments,
  } = useComments();

  const openCount = comments.filter((c) => c.state === 'open').length;

  const tooltipClass = vertical ? 'tt-left' : 'tt-below';

  return (
    <div
      data-comment-ui="true"
      data-review-mode="true"
      style={{
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        onClick={toggleSidebar}
        className={`theme-toggle ${tooltipClass}`}
        aria-label={sidebarOpen ? 'Close comment list' : `Open comment list · ${openCount} open`}
        aria-pressed={sidebarOpen}
        style={{
          background: sidebarOpen ? 'var(--hairline-strong)' : undefined,
          color: sidebarOpen ? 'var(--ink-primary)' : undefined,
        }}
      >
        <PanelRight size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={toggleCommentMode}
        className={`theme-toggle ${tooltipClass}`}
        aria-label={
          commentMode
            ? `Exit comment mode · hide ${openCount} comment${openCount === 1 ? '' : 's'}`
            : `Enter comment mode · show ${openCount} comment${openCount === 1 ? '' : 's'}`
        }
        aria-pressed={commentMode}
        style={{
          background: commentMode ? 'var(--accent)' : undefined,
          color: commentMode ? '#fff' : undefined,
          border: commentMode ? '1px solid var(--accent)' : undefined,
          position: 'relative',
        }}
      >
        <MessageSquare size={16} aria-hidden="true" />
        {openCount > 0 && !commentMode && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 14,
              height: 14,
              padding: '0 3px',
              borderRadius: 7,
              background: 'var(--accent)',
              color: '#fff',
              fontSize: 9,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              // Explicit so the badge never gets occluded by a later-painted
              // sibling that didn't expect it. Stays within the header's
              // (z-40) stacking context.
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            {openCount > 9 ? '9+' : openCount}
          </span>
        )}
      </button>
    </div>
  );
}
